import type { LanguageCode } from "../hooks/useLanguage";
import { fetchJSON, getWpBaseUrl } from "./api";

const TO_ENGLISH: Record<string, string> = {
  "/": "/en",
  "/kontakt": "/en/contact",
  "/start": "/en/home",
  "/impressum": "/en/imprint",
  "/dokumente": "/en/documents",
  "/lizenz": "/en/license",
  "/datenschutzerklaerung": "/en/privacy-policy",
  "/cchs-regelwerk": "/en/cchs-rulebook",
  "/cchs-regelwerk-manifest": "/en/cchs-rulebook-manifest",
  "/corecode-universe-welt": "/en/corecode-universe-world",
  "/corecode-universe-welt-manifest": "/en/corecode-universe-world-manifest",
  "/editionen": "/en/editions",
  "/das-konsequenz-framework-manifest": "/en/the-consequence-framework-manifest",
  "/the-consequence-framework-regelwerk": "/en/the-consequence-framework-rulebook",
  "/blog": "/en/news",
  "/updates": "/en/updates-more"
};

const TO_GERMAN: Record<string, string> = {
  "/en": "/",
  "/en/contact": "/kontakt",
  "/en/home": "/start",
  "/en/imprint": "/impressum",
  "/en/documents": "/dokumente",
  "/en/license": "/lizenz",
  "/en/privacy-policy": "/datenschutzerklaerung",
  "/en/cchs-rulebook": "/cchs-regelwerk",
  "/en/cchs-rulebook-manifest": "/cchs-regelwerk-manifest",
  "/en/corecode-universe-world": "/corecode-universe-welt",
  "/en/corecode-universe-world-manifest": "/corecode-universe-welt-manifest",
  "/en/editions": "/editionen",
  "/en/the-consequence-framework-manifest": "/das-konsequenz-framework-manifest",
  "/en/the-consequence-framework-rulebook": "/the-consequence-framework-regelwerk",
  "/en/blog": "/blog",
  "/en/news": "/blog",
  "/en/updates-more": "/updates"
};

const getEnglishHref = (path: string) => {
  if (!path) return "/en";
  if (path === "/en" || path.startsWith("/en/")) return path;
  if (TO_ENGLISH[path]) return TO_ENGLISH[path];
  if (path.startsWith("/blog/")) return `/en/news/${path.replace(/^\/blog\//, "")}`;
  return path === "/" ? "/en" : `/en${path}`;
};

const getGermanHref = (path: string) => {
  if (!path) return "/";
  if (!path.startsWith("/en")) return path;
  if (TO_GERMAN[path]) return TO_GERMAN[path];
  if (path.startsWith("/en/news/")) return `/blog/${path.replace(/^\/en\/news\//, "")}`;
  if (path.startsWith("/en/blog/")) return `/blog/${path.replace(/^\/en\/blog\//, "")}`;
  if (path === "/en") return "/";
  return path.replace(/^\/en(\/|$)/, "/");
};

export const getLanguageSwitch = (path: string, lang: LanguageCode) => ({
  label: lang === "en" ? "DE" : "EN",
  href: lang === "en" ? getGermanHref(path) : getEnglishHref(path)
});

export const getFooterSectionTitle = (lang: LanguageCode) => (lang === "en" ? "Formalities" : "Formalien");

type WPTranslationEntity = {
  id?: number;
  slug?: string;
  link?: string;
  translations?: Record<string, number>;
};

const switchCache = new Map<string, string>();

const normalizePath = (path: string) => {
  if (!path) return "/";
  const [withoutHash] = path.split("#");
  const [withoutQuery] = withoutHash.split("?");
  if (!withoutQuery) return "/";
  return withoutQuery.endsWith("/") && withoutQuery.length > 1 ? withoutQuery.slice(0, -1) : withoutQuery;
};

const toContentPath = (path: string, lang: LanguageCode) => {
  if (lang === "de") return path;
  if (path === "/en") return "/";
  return path.replace(/^\/en(?=\/|$)/, "") || "/";
};

const toTargetPath = (contentPath: string, targetLang: LanguageCode) => {
  if (targetLang === "de") return contentPath;
  if (contentPath === "/") return "/en";
  return `/en${contentPath}`;
};

const readPathFromLink = (link?: string) => {
  if (!link) return null;
  try {
    const pathname = new URL(link).pathname || "/";
    return normalizePath(pathname);
  } catch {
    return null;
  }
};

const fetchBySlug = async (resource: "pages" | "posts" | "categories", slug: string, lang: LanguageCode) => {
  const query = `/wp/v2/${resource}?slug=${encodeURIComponent(
    slug
  )}&lang=${encodeURIComponent(lang)}&_fields=id,slug,link,translations`;
  const data = await fetchJSON<WPTranslationEntity[]>(query, { baseUrl: getWpBaseUrl() });
  return data?.[0] ?? null;
};

const fetchById = async (resource: "pages" | "posts" | "categories", id: number) => {
  const query = `/wp/v2/${resource}/${id}?_fields=id,slug,link`;
  return fetchJSON<WPTranslationEntity>(query, { baseUrl: getWpBaseUrl() });
};

const resolveTranslatedSlug = async (
  resource: "pages" | "posts" | "categories",
  slug: string,
  sourceLang: LanguageCode,
  targetLang: LanguageCode
) => {
  const source = await fetchBySlug(resource, slug, sourceLang);
  const targetId = source?.translations?.[targetLang];
  if (!targetId) return null;
  const target = await fetchById(resource, targetId);
  return target?.slug || null;
};

const resolveBlogPath = async (
  contentPath: string,
  sourceLang: LanguageCode,
  targetLang: LanguageCode
): Promise<string | null> => {
  if (contentPath === "/blog" || contentPath === "/news") {
    return targetLang === "en" ? "/en/news" : "/blog";
  }
  const blogMatch = contentPath.match(/^\/(?:blog|news)\/([^/]+)$/);
  if (!blogMatch) return null;
  const sourceSlug = blogMatch[1];
  const postSlug = await resolveTranslatedSlug("posts", sourceSlug, sourceLang, targetLang);
  if (postSlug) {
    const targetBase = targetLang === "en" ? "/en/news" : "/blog";
    return `${targetBase}/${postSlug}`;
  }
  const categorySlug = await resolveTranslatedSlug("categories", sourceSlug, sourceLang, targetLang);
  if (categorySlug) {
    const targetBase = targetLang === "en" ? "/en/news" : "/blog";
    return `${targetBase}/${categorySlug}`;
  }
  return null;
};

const resolvePagePath = async (
  contentPath: string,
  sourceLang: LanguageCode,
  targetLang: LanguageCode
): Promise<string | null> => {
  if (contentPath === "/") return toTargetPath("/", targetLang);
  const segments = contentPath.split("/").filter(Boolean);
  const sourceSlug = segments[segments.length - 1];
  if (!sourceSlug) return null;
  const source = await fetchBySlug("pages", sourceSlug, sourceLang);
  const targetId = source?.translations?.[targetLang];
  if (!targetId) return null;
  const target = await fetchById("pages", targetId);
  const linkedPath = readPathFromLink(target?.link);
  if (!linkedPath) return null;
  return linkedPath;
};

export const resolveLanguageSwitchHref = async (path: string, lang: LanguageCode): Promise<string> => {
  const normalized = normalizePath(path);
  const cacheKey = `${lang}:${normalized}`;
  const cached = switchCache.get(cacheKey);
  if (cached) return cached;

  const fallback = lang === "en" ? getGermanHref(normalized) : getEnglishHref(normalized);
  const targetLang: LanguageCode = lang === "en" ? "de" : "en";
  const contentPath = toContentPath(normalized, lang);

  try {
    const blogPath = await resolveBlogPath(contentPath, lang, targetLang);
    if (blogPath) {
      switchCache.set(cacheKey, blogPath);
      return blogPath;
    }
    const pagePath = await resolvePagePath(contentPath, lang, targetLang);
    if (pagePath) {
      switchCache.set(cacheKey, pagePath);
      return pagePath;
    }
  } catch {
    // Fall through to deterministic local fallback if WP translation lookup fails.
  }

  switchCache.set(cacheKey, fallback);
  return fallback;
};
